import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  ConfigProvider,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  theme,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import {
  getBoletos,
  emitirBoletos,
  liquidarBoletos,
  cancelarBoletos,
} from "@/services/boleto";
import { getUnidades } from "@/services/unidade";
import { BoletoType, StatusBoleto, StatusBoletoLabel, StatusBoletoCor } from "@/types/BoletoType";
import TrocaModal from "./Modals/trocaModal";
import { usePermissions } from "@/hooks/usePermissions";

dayjs.locale("pt-br");

const { RangePicker } = DatePicker;

const STATUS_POR_ABA: Record<string, StatusBoleto[]> = {
  emissao: [StatusBoleto.AEmitir, StatusBoleto.EmAberto],
  emAberto: [StatusBoleto.EmAberto],
  emAtraso: [StatusBoleto.EmAtraso],
  liquidados: [StatusBoleto.Liquidado],
  cancelados: [StatusBoleto.Cancelado],
};

const formatValor = (valor?: number) =>
  valor != null
    ? valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "-";

const formatData = (data?: string) => (data ? dayjs(data).format("DD/MM/YYYY") : "-");

type GrupoAluno = {
  key: string;
  alunoId: number;
  matricula?: number;
  unidadeNome?: string;
  nomeAluno?: string;
  quantidade: number;
  valorTotal: number;
  boletos: BoletoType[];
};

export default function ConsultaBoleto() {
  const { canRecebimentoFinanceiro } = usePermissions();
  const [abaAtiva, setAbaAtiva] = useState("emissao");
  const [boletos, setBoletos] = useState<BoletoType[]>([]);
  const [unidades, setUnidades] = useState<any[]>([]);
  const [unidadeIds, setUnidadeIds] = useState<number[]>([]);
  const [periodo, setPeriodo] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [busca, setBusca] = useState("");
  const [filtroEmissao, setFiltroEmissao] = useState<"todos" | "aEmitir" | "emitidos">("aEmitir");
  const [selecionados, setSelecionados] = useState<number[]>([]);

  const [liquidacaoModalAberto, setLiquidacaoModalAberto] = useState(false);
  const [liquidacaoIds, setLiquidacaoIds] = useState<number[]>([]);
  const [dataLiquidacao, setDataLiquidacao] = useState<Dayjs | null>(dayjs());

  const [cancelarBo, setCancelarBo] = useState<BoletoType | null>(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState("");

  const [trocaBoleto, setTrocaBoleto] = useState<BoletoType | null>(null);

  const carregarBoletos = useCallback(async () => {
    try {
      const filtro = {
        status: STATUS_POR_ABA[abaAtiva],
        unidadeIds: unidadeIds.length > 0 ? unidadeIds : undefined,
        dataInicio: periodo?.[0] ? periodo[0].format("YYYY-MM-DD") : undefined,
        dataFim: periodo?.[1] ? periodo[1].format("YYYY-MM-DD") : undefined,
        busca: busca.trim() || undefined,
      };
      const { data } = await getBoletos(filtro);
      setBoletos(data ?? []);
      setSelecionados([]);
    } catch {
      message.error("Erro ao carregar os boletos.");
    }
  }, [abaAtiva, unidadeIds, periodo, busca]);

  useEffect(() => {
    carregarBoletos();
  }, [carregarBoletos]);

  useEffect(() => {
    getUnidades()
      .then(({ data }) => setUnidades(data ?? []))
      .catch(() => message.error("Erro ao carregar unidades."));
  }, []);

  const executar = async (acao: () => Promise<any>, sucesso: string) => {
    try {
      await acao();
      message.success(sucesso);
      carregarBoletos();
    } catch (error: any) {
      message.error(error?.response?.data ?? "Erro ao executar a operação.");
    }
  };

  // ----------------------------------------------------------------- colunas
  const colunasBase: ColumnsType<BoletoType> = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: StatusBoleto, record: BoletoType) => (
        <Tag color={StatusBoletoCor[status]}>{record.statusLabel ?? StatusBoletoLabel[status] ?? status}</Tag>
      ),
    },
    { title: "Matrícula", dataIndex: "matricula", key: "matricula" },
    { title: "Unidade", dataIndex: "unidadeNome", key: "unidadeNome" },
    { title: "Aluno", dataIndex: "nomeAluno", key: "nomeAluno" },
    { title: "Parcela", dataIndex: "parcela", key: "parcela" },
    {
      title: "Vencimento",
      dataIndex: "dataVencimento",
      key: "dataVencimento",
      render: (v: string) => formatData(v),
    },
    {
      title: "Valor",
      dataIndex: "valor",
      key: "valor",
      render: (v: number) => formatValor(v),
    },
  ];

  // --------------------------------------------------------- aba 1: emissão
  const boletosEmissao = useMemo(() => {
    if (filtroEmissao === "aEmitir")
      return boletos.filter((b) => b.status === StatusBoleto.AEmitir);
    if (filtroEmissao === "emitidos")
      return boletos.filter((b) => b.status === StatusBoleto.EmAberto);
    return boletos;
  }, [boletos, filtroEmissao]);

  const gruposAluno: GrupoAluno[] = useMemo(() => {
    const mapa = new Map<string, GrupoAluno>();
    boletosEmissao.forEach((b) => {
      const chave = String(b.alunoId);
      const grupo = mapa.get(chave) ?? {
        key: chave,
        alunoId: b.alunoId,
        matricula: b.matricula,
        unidadeNome: b.unidadeNome,
        nomeAluno: b.nomeAluno,
        quantidade: 0,
        valorTotal: 0,
        boletos: [],
      };
      grupo.quantidade += 1;
      grupo.valorTotal += b.valor ?? 0;
      grupo.boletos.push(b);
      mapa.set(chave, grupo);
    });
    return Array.from(mapa.values());
  }, [boletosEmissao]);

  const colunasGrupo: ColumnsType<GrupoAluno> = [
    { title: "Matrícula", dataIndex: "matricula", key: "matricula" },
    { title: "Unidade", dataIndex: "unidadeNome", key: "unidadeNome" },
    { title: "Aluno", dataIndex: "nomeAluno", key: "nomeAluno" },
    { title: "Qtd. Boletos", dataIndex: "quantidade", key: "quantidade" },
    {
      title: "Valor Total",
      dataIndex: "valorTotal",
      key: "valorTotal",
      render: (v: number) => formatValor(v),
    },
    {
      title: "Ação",
      key: "acao",
      render: (_, grupo) => {
        const pendentes = grupo.boletos.filter((b) => b.status === StatusBoleto.AEmitir);
        if (pendentes.length === 0) return <Tag color="blue">Emitidos</Tag>;
        if (!canRecebimentoFinanceiro) return null;
        return (
          <Popconfirm
            title="Confirmar emissão?"
            description={`Confirmar a emissão de ${pendentes.length} boleto(s)?`}
            okText="Sim"
            cancelText="Não"
            onConfirm={() =>
              executar(
                () => emitirBoletos(pendentes.map((b) => b.id)),
                "Emissão confirmada com sucesso!"
              )
            }
          >
            <Button type="primary" size="small">
              Confirmar emissão
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  const colunasBoletosDoGrupo: ColumnsType<BoletoType> = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: StatusBoleto, record: BoletoType) => (
        <Tag color={StatusBoletoCor[status]}>{record.statusLabel ?? StatusBoletoLabel[status] ?? status}</Tag>
      ),
    },
    { title: "Parcela", dataIndex: "parcela", key: "parcela" },
    {
      title: "Vencimento",
      dataIndex: "dataVencimento",
      key: "dataVencimento",
      render: (v: string) => formatData(v),
    },
    {
      title: "Valor",
      dataIndex: "valor",
      key: "valor",
      render: (v: number) => formatValor(v),
    },
    {
      title: "Data Emissão",
      dataIndex: "dataEmissao",
      key: "dataEmissao",
      render: (v: string) => formatData(v),
    },
    {
      title: "Ação",
      key: "acao",
      render: (_, boleto) => (
        <Space size="small">
          {boleto.status === StatusBoleto.AEmitir && canRecebimentoFinanceiro && (
            <Popconfirm
              title="Confirmar a emissão deste boleto?"
              okText="Sim"
              cancelText="Não"
              onConfirm={() =>
                executar(() => emitirBoletos([boleto.id]), "Emissão confirmada com sucesso!")
              }
            >
              <Button type="link" size="small">
                Confirmar emissão
              </Button>
            </Popconfirm>
          )}
          <Button type="link" size="small" onClick={() => setTrocaBoleto(boleto)}>
            Trocar pagamento
          </Button>
        </Space>
      ),
    },
  ];

  // ---------------------------------------------- abas 2 e 3: aberto/atraso
  const totalSelecionado = useMemo(() => {
    const itens = boletos.filter((b) => selecionados.includes(b.id));
    return {
      quantidade: itens.length,
      valor: itens.reduce((soma, b) => soma + (b.valor ?? 0), 0),
    };
  }, [boletos, selecionados]);

  const abrirLiquidacao = (ids: number[]) => {
    setLiquidacaoIds(ids);
    setDataLiquidacao(dayjs());
    setLiquidacaoModalAberto(true);
  };

  const confirmarLiquidacao = () => {
    if (!dataLiquidacao) {
      message.warning("Informe a data da liquidação.");
      return;
    }
    setLiquidacaoModalAberto(false);
    executar(
      () => liquidarBoletos(liquidacaoIds, dataLiquidacao.format("YYYY-MM-DD")),
      "Liquidação confirmada com sucesso!"
    );
  };

  const colunasAcaoAbertoAtraso = (emAtraso: boolean): ColumnsType<BoletoType> => [
    ...colunasBase,
    {
      title: "Data Emissão",
      dataIndex: "dataEmissao",
      key: "dataEmissao",
      render: (v: string) => formatData(v),
    },
    ...(emAtraso
      ? ([
          {
            title: "Dias em Atraso",
            dataIndex: "dataVencimento",
            key: "diasAtraso",
            render: (v: string) => (v ? dayjs().startOf("day").diff(dayjs(v).startOf("day"), "day") : "-"),
          },
        ] as ColumnsType<BoletoType>)
      : []),
    {
      title: "Ação",
      key: "acao",
      render: (_, boleto) => (
        <Space size="small">
          {canRecebimentoFinanceiro && (
            <Button type="link" size="small" onClick={() => abrirLiquidacao([boleto.id])}>
              Liquidar
            </Button>
          )}
          <Button
            type="link"
            danger
            size="small"
            onClick={() => {
              setCancelarBo(boleto);
              setMotivoCancelamento("");
            }}
          >
            Cancelar
          </Button>
          <Button type="link" size="small" onClick={() => setTrocaBoleto(boleto)}>
            Trocar pagamento
          </Button>
        </Space>
      ),
    },
  ];

  // ------------------------------------------------------ aba 4: liquidados
  const colunasLiquidados: ColumnsType<BoletoType> = [
    ...colunasBase,
    {
      title: "Data Emissão",
      dataIndex: "dataEmissao",
      key: "dataEmissao",
      render: (v: string) => formatData(v),
    },
    {
      title: "Data Liquidação",
      dataIndex: "dataLiquidacao",
      key: "dataLiquidacao",
      render: (v: string) => formatData(v),
    },
  ];

  // ------------------------------------------------------ aba 5: cancelados
  const colunasCancelados: ColumnsType<BoletoType> = [
    ...colunasBase,
    { title: "Motivo Cancelamento", dataIndex: "motivoCancelamento", key: "motivoCancelamento" },
    { title: "Trocado por", dataIndex: "trocaPagamentoNome", key: "trocaPagamentoNome" },
    { title: "CV", dataIndex: "trocaCv", key: "trocaCv" },
    {
      title: "Data Transação",
      dataIndex: "trocaDataTransacao",
      key: "trocaDataTransacao",
      render: (v: string) => formatData(v),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selecionados,
    onChange: (keys: React.Key[]) => setSelecionados(keys as number[]),
  };

  const tabelaAbertoAtraso = (emAtraso: boolean) => (
    <>
      <div className="mb-4 flex items-center gap-6">
        {canRecebimentoFinanceiro && (
          <Button
            type="primary"
            disabled={selecionados.length === 0}
            onClick={() => abrirLiquidacao(selecionados)}
          >
            Liquidar selecionados
          </Button>
        )}
        <span className="text-white">
          {totalSelecionado.quantidade} boleto(s) selecionado(s) —{" "}
          <strong>{formatValor(totalSelecionado.valor)}</strong>
        </span>
      </div>
      <Table
        columns={colunasAcaoAbertoAtraso(emAtraso)}
        dataSource={boletos}
        rowKey="id"
        rowSelection={rowSelection}
      />
    </>
  );

  const abas = [
    {
      key: "emissao",
      label: "Emissão",
      children: (
        <>
          <div className="mb-4">
            <Radio.Group
              value={filtroEmissao}
              onChange={(e) => setFiltroEmissao(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              options={[
                { label: "⏳ A emitir", value: "aEmitir" },
                { label: "✅ Emitidos", value: "emitidos" },
                { label: "Todos", value: "todos" },
              ]}
            />
          </div>
          <Table
            columns={colunasGrupo}
            dataSource={gruposAluno}
            rowKey="key"
            expandable={{
              expandedRowRender: (grupo) => (
                <Table
                  columns={colunasBoletosDoGrupo}
                  dataSource={grupo.boletos}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              ),
            }}
          />
        </>
      ),
    },
    {
      key: "emAberto",
      label: "Em Aberto",
      children: tabelaAbertoAtraso(false),
    },
    {
      key: "emAtraso",
      label: "Em Atraso",
      children: tabelaAbertoAtraso(true),
    },
    {
      key: "liquidados",
      label: "Liquidados",
      children: (
        <Table columns={colunasLiquidados} dataSource={boletos} rowKey="id" />
      ),
    },
    {
      key: "cancelados",
      label: "Cancelados",
      children: (
        <Table columns={colunasCancelados} dataSource={boletos} rowKey="id" />
      ),
    },
  ];

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Controle de Boletos</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-8 items-center mb-6">
          <Input
            allowClear
            placeholder="Buscar por nome ou CPF"
            className="w-80"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <Select
            allowClear
            mode="multiple"
            placeholder="Todas as unidades"
            className="w-64"
            value={unidadeIds}
            onChange={(value) => setUnidadeIds(value)}
            options={unidades.map((u: any) => ({ value: u.id, label: u.nome }))}
          />
          <RangePicker
            format="DD/MM/YYYY"
            value={periodo}
            onChange={(datas) => setPeriodo(datas as [Dayjs | null, Dayjs | null] | null)}
            placeholder={["Data inicial", "Data final"]}
          />
        </div>

        <Tabs
          activeKey={abaAtiva}
          onChange={(key) => {
            setAbaAtiva(key);
            setSelecionados([]);
          }}
          items={abas}
        />

        {/* Modal de liquidação */}
        <Modal
          title="Confirmar Liquidação"
          open={liquidacaoModalAberto}
          onOk={confirmarLiquidacao}
          onCancel={() => setLiquidacaoModalAberto(false)}
          okText="Confirmar"
          cancelText="Cancelar"
        >
          <p className="mb-2">
            {liquidacaoIds.length} boleto(s) será(ão) marcado(s) como Liquidado e a(s)
            parcela(s) correspondente(s) como Paga(s).
          </p>
          <Form layout="vertical">
            <Form.Item label="Data da liquidação" required>
              <DatePicker
                format="DD/MM/YYYY"
                value={dataLiquidacao}
                onChange={(data) => setDataLiquidacao(data)}
                className="w-full"
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* Modal de cancelamento */}
        <Modal
          title={`Cancelar Boleto — parcela ${cancelarBo?.parcela ?? ""}`}
          open={cancelarBo !== null}
          onOk={() => {
            if (!motivoCancelamento.trim()) {
              message.warning("Informe o motivo do cancelamento.");
              return;
            }
            const id = cancelarBo!.id;
            setCancelarBo(null);
            executar(() => cancelarBoletos([id], motivoCancelamento), "Cancelamento registrado!");
          }}
          onCancel={() => setCancelarBo(null)}
          okText="Confirmar"
          cancelText="Cancelar"
        >
          <Form layout="vertical">
            <Form.Item label="Motivo do cancelamento" required>
              <Input.TextArea
                rows={3}
                maxLength={200}
                value={motivoCancelamento}
                onChange={(e) => setMotivoCancelamento(e.target.value)}
                placeholder="Ex.: negociação direta com o aluno"
              />
            </Form.Item>
          </Form>
        </Modal>

        <TrocaModal
          boleto={trocaBoleto}
          onClose={() => setTrocaBoleto(null)}
          onDone={() => {
            setTrocaBoleto(null);
            carregarBoletos();
          }}
        />
      </ConfigProvider>
    </>
  );
}
